import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import type {
	ChildResult,
	Command,
	CommandSections,
} from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { FullWidthText } from '../full-width-text/index.tsx';
import type { GetQuickSearchChildResultProps, UseGetChildResult } from '../types.tsx';
import {
	getQuickSearchBoardScope,
	getQuickSearchFilterScope,
	getQuickSearchIssueScope,
	getQuickSearchProjectScope,
	getQuickSearchPlanScope,
} from '../utils.tsx';
import {
	FULL_ERROR_TEST_ID,
	NO_RECENT_RESULTS_TEST_ID,
	NO_SEARCH_RESULTS_TEST_ID,
	PARTIAL_ERROR_TEST_ID,
} from './constants.tsx';
import { messages } from './messages.tsx';
import { NoRecentResults } from './no-recent-results/index.tsx';

export const useGetErrorChildResult: UseGetChildResult = () => {
	const { formatMessage, formatList } = useIntl();

	const getChildResult = useCallback(
		({ search, quickSearchResponseData, error }: GetQuickSearchChildResultProps): ChildResult => {
			const projectsGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.projectsGalaxia
				: messages.projects;
			const commands: Command[] = [];
			const sections: CommandSections = {};

			const isSearching = Boolean(search);

			const issueScope = getQuickSearchIssueScope(quickSearchResponseData);
			const projectScope = getQuickSearchProjectScope(quickSearchResponseData);
			const boardScope = getQuickSearchBoardScope(quickSearchResponseData);
			const filterScope = getQuickSearchFilterScope(quickSearchResponseData);
			const planScope = getQuickSearchPlanScope(quickSearchResponseData);

			const errorScopes = [
				issueScope?.error
					? formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.issuesIssueTermRefresh
								: messages.issues,
						)
					: undefined,
				projectScope?.error ? formatMessage(projectsGalaxia) : undefined,
				boardScope?.error ? formatMessage(messages.boards) : undefined,
				filterScope?.error ? formatMessage(messages.filters) : undefined,
				planScope?.error ? formatMessage(messages.plans) : undefined,
			];
			const errors = errorScopes.filter(Boolean);

			// If there is an api error or the api scopes are all errors
			if (error || errorScopes.length === errors.length) {
				const getMessage = () => {
					if (isSearching) {
						return messages.searchFullError;
					}
					return fg('jira-issue-terminology-refresh-m3')
						? messages.recentFullErrorIssueTermRefresh
						: messages.recentFullError;
				};
				const message = formatMessage(getMessage());
				commands.push({
					id: 'full-error',
					name: message,
					components: {
						Name: () => (
							<CommandErrorWrapper data-testid={FULL_ERROR_TEST_ID}>
								<FullWidthText>{message}</FullWidthText>
							</CommandErrorWrapper>
						),
					},
					keywords: search,
				});
				return { sections, commands };
			}

			// If the api scopes are partially errored
			if (errors.length) {
				const formattedErrors = formatList(errors, { style: 'long', type: 'conjunction' });
				const message = isSearching
					? formatMessage(messages.searchPartialError, {
							errorList: formattedErrors,
						})
					: formatMessage(messages.recentPartialError, { errorList: formattedErrors });
				commands.push({
					id: 'partial-error',
					name: formatMessage(messages.searchFullError), // todo fix this
					components: {
						Name: () => (
							<CommandErrorWrapper data-testid={PARTIAL_ERROR_TEST_ID}>
								<FullWidthText>{message}</FullWidthText>
							</CommandErrorWrapper>
						),
					},
					keywords: search,
				});
				return { sections, commands };
			}

			const noSearchResults =
				!issueScope?.results?.length &&
				!projectScope?.results?.length &&
				!boardScope?.results?.length &&
				!filterScope?.results?.length &&
				!planScope?.results?.length;

			if (noSearchResults) {
				if (isSearching) {
					commands.push({
						id: 'no-search-results',
						name: formatMessage(messages.searchFullError),
						keywords: search,
						components: {
							Name: () => (
								<CommandErrorWrapper data-testid={NO_SEARCH_RESULTS_TEST_ID}>
									<FullWidthText>
										{formatMessage(
											fg('jira-issue-terminology-refresh-m3')
												? messages.searchNoResultsIssueTermRefresh
												: messages.searchNoResults,
										)}
									</FullWidthText>
								</CommandErrorWrapper>
							),
						},
					});
				} else {
					const message = formatMessage(messages.noRecentResults);
					commands.push({
						id: 'no-recent-results',
						name: message,
						keywords: search,
						components: {
							Name: () => (
								<CommandErrorWrapper data-testid={NO_RECENT_RESULTS_TEST_ID}>
									<NoRecentResults message={message} />
								</CommandErrorWrapper>
							),
						},
					});
				}
				return { sections, commands };
			}

			return {
				sections,
				commands,
			};
		},
		[formatList, formatMessage],
	);

	return { getChildResult };
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommandErrorWrapper = styled.div({
	paddingTop: token('space.200'),
	paddingBottom: token('space.100'),
});
